import { Action, Dispatch } from 'redux';
import {
  API_DEFAULT_RETRIES,
  CTUI,
  KEVALIN_COLLECTIONS,
  NONE_SELECTED,
  NPST,
  NPS_RELATED_EVENT_CATEGORIES,
  NPS_RELATED_EVENT_TYPES,
} from 'appConstants';
import { ApiRequest, BrowserUtils, KevalinUtils } from 'services';
import { NPSFeedback } from 'types';

/* Types */

export interface NpsAction extends Action {
  comment?: string;
  score?: number | undefined;
  visibility?: boolean;
  successState?: boolean;
}

export interface Rating {
  readonly _isNpstVisible: boolean;
  readonly _isSuccessState: boolean;
  readonly comment: string;
  readonly score: number | undefined;
}

/* Initial State */

const initialState = {
  _isNpstVisible: false,
  _isSuccessState: false,
  comment: '',
  score: undefined,
} as Rating;

/* Actions */

enum NpsActions {
  RESET_NPS = 'RESET_NPS',
  SET_NPS_COMMENT = 'SET_NPS_COMMENT',
  SET_NPS_SCORE = 'SET_NPS_SCORE',
  SET_NPST_MODAL_VISIBILITY = 'SET_NPST_MODAL_VISIBILITY',
  SET_NPST_MODAL_STATE = 'SET_NPST_MODAL_STATE',
}

const resetNps = (): NpsAction => ({
  type: NpsActions.RESET_NPS,
});

const setNpsComment = (comment: string): NpsAction => ({
  type: NpsActions.SET_NPS_COMMENT,
  comment,
});

const setNpsScore = (score: number): NpsAction => ({
  type: NpsActions.SET_NPS_SCORE,
  score,
});

const showNpstModal = (visibility: boolean): NpsAction => ({
  type: NpsActions.SET_NPST_MODAL_VISIBILITY,
  visibility,
});

const setNpstModalState = (successState: boolean): NpsAction => ({
  type: NpsActions.SET_NPST_MODAL_STATE,
  successState,
});

/* Reducer */

const npsReducer = (state = initialState, action: NpsAction) => {
  switch (action.type) {
    case NpsActions.SET_NPS_COMMENT:
      return {
        ...state,
        comment: action.comment,
      };
    case NpsActions.SET_NPS_SCORE:
      return {
        ...state,
        score: action.score,
      };
    case NpsActions.RESET_NPS:
      return {
        ...state,
        score: undefined,
        comment: '',
        _isSuccessState: false,
      };
    case NpsActions.SET_NPST_MODAL_STATE:
      return {
        ...state,
        _isSuccessState: action.successState,
      };
    case NpsActions.SET_NPST_MODAL_VISIBILITY:
      return {
        ...state,
        _isNpstVisible: action.visibility,
      };
    default:
      return state;
  }
};

const storeNPSMetrics = (
  ticketIds: Array<string>,
  score?: number,
  npsComment?: string,
) => async (dispatch: Dispatch<NpsAction>) => {
  const feedback: NPSFeedback = {
    rating: {
      score: score || score === 0 ? score : NONE_SELECTED,
      comment: npsComment ? npsComment : NONE_SELECTED,
    },
  };
  const payload = {
    eventData: {
      portal: CTUI,
      surveyType: NPST,
      username: window.PORTAL_DATA.username,
      domainId: window.PORTAL_DATA.domainId,
      accountType: window.PORTAL_DATA.domainId.includes('dedicated')
        ? 'dedicated'
        : 'cloud',
      referrer: window.location.href,
      userAgent: BrowserUtils.getBrowserDetails(),
      relatedEvents: KevalinUtils.getRelatedEvents(
        NPS_RELATED_EVENT_TYPES.ticket,
        NPS_RELATED_EVENT_CATEGORIES.service,
        ticketIds,
        CTUI,
      ),
      ...feedback,
    },
  };
  return ApiRequest.post(
    KevalinUtils.getKevalinURL(KEVALIN_COLLECTIONS.CUSTOMER_NPS),
    payload,
    {
      retries: API_DEFAULT_RETRIES,
    },
  );
};

export {
  NpsActions,
  npsReducer,
  resetNps,
  setNpsComment,
  setNpsScore,
  setNpstModalState,
  showNpstModal,
  storeNPSMetrics,
};
